import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Button, Card, SUB_CONFIRM_LAYER, TextButton, useConfirmDialog } from '@safetyhub/ui';
import {
  adminApi,
  FeatureFlagOverride,
  FeatureFlagGroupOverride,
  FeatureFlagSearchTarget,
  FeatureFlagGroup,
} from '../../lib/api';
import { useToastStore } from '../../stores/toast';

interface OverrideListProps {
  flagId: string;
  overrides: FeatureFlagOverride[];
  groupOverrides?: FeatureFlagGroupOverride[];
}

// 오버라이드 *대상 타입*을 색으로 가르는 범례라 blue는 brand로 옮기지 않는다 —
// 조직(purple)과 인디고가 붙어 사용자/조직 구분이 무너진다. 아래 targetType 배지도 같은 체계.
const LABEL_MAP: Record<FeatureFlagSearchTarget['label'], { text: string; className: string }> = {
  superAdmin: { text: '관리자', className: 'bg-red-100 text-red-700' },
  user: { text: '사용자', className: 'bg-blue-100 text-blue-700' },
  organization: { text: '조직', className: 'bg-purple-100 text-purple-700' },
};

type TargetType = 'USER' | 'ORGANIZATION' | 'GROUP';

function TargetSearchInput({
  targetType,
  onSelect,
  selected,
  onClear,
}: {
  targetType: 'USER' | 'ORGANIZATION';
  onSelect: (target: FeatureFlagSearchTarget) => void;
  selected: FeatureFlagSearchTarget | null;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FeatureFlagSearchTarget[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const res = await adminApi.searchFeatureFlagTargets(targetType, q);
        if (res.success && res.data) {
          setResults(res.data);
          setIsOpen(res.data.length > 0);
        }
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [targetType],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  // targetType 변경 시 초기화
  useEffect(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  }, [targetType]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (selected) {
    const labelInfo = LABEL_MAP[selected.label];
    return (
      <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded bg-gray-50 text-sm">
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${labelInfo.className}`}>
          {labelInfo.text}
        </span>
        <span className="font-medium">{selected.name}</span>
        {selected.email && (
          <span className="text-gray-500">{selected.email}</span>
        )}
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-gray-400 hover:text-gray-600"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setIsOpen(true)}
        placeholder={targetType === 'USER' ? '이름 또는 이메일 검색 (2글자 이상)' : '조직 이름 검색 (2글자 이상)'}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
        </div>
      )}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-card shadow-lg max-h-60 overflow-auto">
          {results.map((t) => {
            const labelInfo = LABEL_MAP[t.label];
            return (
              <button
                key={`${t.label}-${t.id}`}
                type="button"
                onClick={() => {
                  onSelect(t);
                  setQuery('');
                  setIsOpen(false);
                  setResults([]);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-sm"
              >
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${labelInfo.className}`}>
                  {labelInfo.text}
                </span>
                <span className="font-medium truncate">{t.name}</span>
                {t.email && (
                  <span className="text-gray-500 truncate">{t.email}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupSearchInput({
  onSelect,
  selectedGroup,
  onClear,
}: {
  onSelect: (group: FeatureFlagGroup) => void;
  selectedGroup: FeatureFlagGroup | null;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'feature-flag-groups', 'search', query],
    queryFn: () => adminApi.getFeatureFlagGroups(query || undefined),
    enabled: query.length >= 1,
  });

  const groups = data?.data ?? [];

  useEffect(() => {
    if (groups.length > 0 && query.length >= 1) {
      setIsOpen(true);
    }
  }, [groups, query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (selectedGroup) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded bg-gray-50 text-sm">
        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
          그룹
        </span>
        <span className="font-medium">{selectedGroup.name}</span>
        <span className="text-gray-500 text-xs">({selectedGroup.memberCount}명)</span>
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-gray-400 hover:text-gray-600"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => groups.length > 0 && setIsOpen(true)}
        placeholder="그룹 이름 검색"
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
        </div>
      )}
      {isOpen && groups.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-card shadow-lg max-h-60 overflow-auto">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => {
                onSelect(g);
                setQuery('');
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-sm"
            >
              <span className="px-1.5 py-0.5 rounded text-xs font-medium shrink-0 bg-green-100 text-green-700">
                그룹
              </span>
              <span className="font-medium truncate">{g.name}</span>
              <span className="text-gray-500 text-xs">({g.memberCount}명)</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OverrideList({ flagId, overrides, groupOverrides = [] }: OverrideListProps) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  // 피처 플래그 상세(base Modal z-[70]) 안에서 열리므로 확인은 한 층 위(`SUB_CONFIRM_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();
  const [targetType, setTargetType] = useState<TargetType>('USER');
  const [selectedTarget, setSelectedTarget] = useState<FeatureFlagSearchTarget | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<FeatureFlagGroup | null>(null);
  const [overrideValue, setOverrideValue] = useState('true');

  const addMutation = useMutation({
    mutationFn: () =>
      adminApi.addFeatureFlagOverride(flagId, {
        targetType: targetType as 'USER' | 'ORGANIZATION',
        targetId: selectedTarget!.id,
        value: overrideValue === 'true',
      }),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flagId] });
        setSelectedTarget(null);
        showToast('오버라이드가 추가되었습니다.', 'success');
      } else {
        showToast(result.error?.message || '추가에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('오버라이드 추가 중 오류가 발생했습니다.', 'error');
    },
  });

  const addGroupMutation = useMutation({
    mutationFn: () =>
      adminApi.addFeatureFlagGroupOverride(flagId, {
        groupId: selectedGroup!.id,
        value: overrideValue === 'true',
      }),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flagId] });
        setSelectedGroup(null);
        showToast('그룹 오버라이드가 추가되었습니다.', 'success');
      } else {
        showToast(result.error?.message || '추가에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('그룹 오버라이드 추가 중 오류가 발생했습니다.', 'error');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (overrideId: string) =>
      adminApi.removeFeatureFlagOverride(flagId, overrideId),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flagId] });
        showToast('오버라이드가 삭제되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('삭제 중 오류가 발생했습니다.', 'error');
    },
  });

  const removeGroupMutation = useMutation({
    mutationFn: (groupId: string) =>
      adminApi.removeFeatureFlagGroupOverride(flagId, groupId),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flagId] });
        showToast('그룹 오버라이드가 삭제되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('삭제 중 오류가 발생했습니다.', 'error');
    },
  });

  const toggleOverrideMutation = useMutation({
    mutationFn: ({ overrideId, value }: { overrideId: string; value: boolean }) =>
      adminApi.updateFeatureFlagOverrideValue(flagId, overrideId, value),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flagId] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      } else {
        showToast(result.error?.message || '값 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('값 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const toggleGroupOverrideMutation = useMutation({
    mutationFn: ({ groupId, value }: { groupId: string; value: boolean }) =>
      adminApi.updateFeatureFlagGroupOverrideValue(flagId, groupId, value),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags', flagId] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      } else {
        showToast(result.error?.message || '값 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('값 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (targetType === 'GROUP') {
      if (!selectedGroup) return;
      addGroupMutation.mutate();
    } else {
      if (!selectedTarget) return;
      addMutation.mutate();
    }
  };

  // 개별 오버라이드 중 수동(groupLinkId 없음)만 표시
  const manualOverrides = overrides.filter((o) => !o.groupLinkId);

  return (
    <div className="space-y-4">
      {/* 추가 폼 */}
      <form onSubmit={handleAdd} className="flex items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            대상 유형
          </label>
          <select
            value={targetType}
            onChange={(e) => {
              setTargetType(e.target.value as TargetType);
              setSelectedTarget(null);
              setSelectedGroup(null);
            }}
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="USER">사용자</option>
            <option value="ORGANIZATION">조직</option>
            <option value="GROUP">그룹</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            대상 검색
          </label>
          {targetType === 'GROUP' ? (
            <GroupSearchInput
              onSelect={setSelectedGroup}
              selectedGroup={selectedGroup}
              onClear={() => setSelectedGroup(null)}
            />
          ) : (
            <TargetSearchInput
              targetType={targetType}
              onSelect={setSelectedTarget}
              selected={selectedTarget}
              onClear={() => setSelectedTarget(null)}
            />
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            값
          </label>
          <select
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={
            (targetType === 'GROUP' ? addGroupMutation.isPending || !selectedGroup : addMutation.isPending || !selectedTarget)
          }
        >
          {(addMutation.isPending || addGroupMutation.isPending) ? '추가 중...' : '추가'}
        </Button>
      </form>

      {/* 그룹 오버라이드 섹션 */}
      {groupOverrides.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">그룹 오버라이드</h4>
          <Card padding="none" elevation="none" className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="divide-y divide-gray-200">
                {groupOverrides.map((go) => (
                  <tr key={go.id}>
                    <td className="px-4 py-2 text-sm">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                        그룹
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className="font-medium text-gray-900">{go.groupName}</span>
                      <span className="ml-1.5 text-gray-500 text-xs">({go.memberCount}명)</span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <button
                        onClick={() => toggleGroupOverrideMutation.mutate({ groupId: go.groupId, value: !go.value })}
                        disabled={toggleGroupOverrideMutation.isPending}
                        className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 ${
                          go.value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                        title="클릭하여 값 토글"
                      >
                        {String(go.value)}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {new Date(go.createdAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <TextButton
                        tone="danger"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: '그룹 오버라이드 삭제',
                              body: `"${go.groupName}" 그룹 오버라이드를 삭제하시겠습니까?`,
                              confirmLabel: '삭제',
                              danger: true,
                              ...SUB_CONFIRM_LAYER,
                            })
                          ) {
                            removeGroupMutation.mutate(go.groupId);
                          }
                        }}
                        disabled={removeGroupMutation.isPending}
                        className="text-sm"
                      >
                        삭제
                      </TextButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* 개별 오버라이드 목록 */}
      {manualOverrides.length === 0 && groupOverrides.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          등록된 오버라이드가 없습니다.
        </div>
      ) : manualOverrides.length > 0 ? (
        <div>
          {groupOverrides.length > 0 && (
            <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">개별 오버라이드</h4>
          )}
          <Card padding="none" elevation="none" className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    대상 유형
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    대상
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    값
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    생성일
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {manualOverrides.map((o) => (
                  <tr key={o.id}>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          o.targetType === 'USER'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {o.targetType === 'USER' ? '사용자' : '조직'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {o.targetName ? (
                        <div>
                          <span className="font-medium text-gray-900">{o.targetName}</span>
                          {o.targetEmail && (
                            <span className="ml-1.5 text-gray-500 text-xs">{o.targetEmail}</span>
                          )}
                        </div>
                      ) : (
                        <span className="font-mono text-gray-400 text-xs">{o.targetId.substring(0, 12)}...</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <button
                        onClick={() => toggleOverrideMutation.mutate({ overrideId: o.id, value: !o.value })}
                        disabled={toggleOverrideMutation.isPending}
                        className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 ${
                          o.value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                        title="클릭하여 값 토글"
                      >
                        {String(o.value)}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {new Date(o.createdAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <TextButton
                        tone="danger"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: '오버라이드 삭제',
                              body: '이 오버라이드를 삭제하시겠습니까?',
                              confirmLabel: '삭제',
                              danger: true,
                              ...SUB_CONFIRM_LAYER,
                            })
                          ) {
                            removeMutation.mutate(o.id);
                          }
                        }}
                        disabled={removeMutation.isPending}
                        className="text-sm"
                      >
                        삭제
                      </TextButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ) : null}
      {confirmDialog}
    </div>
  );
}
